import type { HttpFailureKind, HttpRequestError } from '@ns/http-request';
import {
  isHttpError,
  isHttpRequestError,
  isHttpThrottleError,
} from '@ns/http-request';

/**
 * The body of a refusal by the proxy or the stub.
 *
 * @remarks
 * Written by `refusalBody` in `@ns/plugin-arm-proxy-backend`. That body also
 * carries `error.name` and `response.statusCode`; this layer reads only the
 * fields listed here.
 */
export interface ProxyRefusalBody {
  readonly error: {
    /** Why the proxy refused, such as `consent-revoked` or `unknown-path`. */
    readonly code: string;
    /** A human-readable explanation written by the proxy. */
    readonly message: string;
    /** Always `'arm-proxy'`: the field that tells a refusal from an Azure error. */
    readonly source: 'arm-proxy';
  };
}

/** A library error whose body is a refusal by the proxy. */
export type ProxyRefusal = HttpRequestError & {
  readonly body: ProxyRefusalBody;
};

/**
 * True when the PROXY refused, false when Azure answered.
 *
 * @remarks
 * The proxy writes `error.source: 'arm-proxy'` into every refusal, and Azure
 * never writes that field. A value that is not an `HttpRequestError` is never a
 * refusal, even when its shape matches.
 *
 * @param error - Whatever a `catch` received.
 * @returns Whether `error` is a refusal, narrowing it so `error.body.error.code`
 * is typed.
 */
export function isProxyRefusal(error: unknown): error is ProxyRefusal {
  if (!isHttpRequestError(error)) return false;
  const inner = errorObjectOf(error.body);
  return (
    inner?.source === 'arm-proxy' &&
    typeof inner.code === 'string' &&
    typeof inner.message === 'string'
  );
}

/** A failure described in the terms `@ns/plugin-picker-shared` classifies. */
export interface FailureDescription {
  /** The coarse family of the failure, as `@ns/http-request` names it. */
  readonly kind: HttpFailureKind;
  /** The `error.code` of a refusal written by the proxy, when it was one. */
  readonly refusalCode?: string;
  /** The status the upstream answered with, when there was an answer. */
  readonly status?: number;
}

/**
 * Describes a caught error in the terms a submit validator classifies.
 *
 * @remarks
 * A throttle is recognised by its class, like `isHttpThrottleError` does, and
 * described as the 429 it stands for.
 *
 * @param error - Whatever a `catch` received.
 * @returns The description, or `undefined` when the error is no failure of this client.
 */
export function describeFailure(
  error: unknown,
): FailureDescription | undefined {
  if (isProxyRefusal(error)) {
    return {
      kind: 'http',
      refusalCode: error.body.error.code,
      status: error.status,
    };
  }
  if (isHttpThrottleError(error)) return { kind: 'http', status: 429 };
  if (isHttpRequestError(error)) return { kind: 'http', status: error.status };
  if (isHttpError(error)) return { kind: error.failureKind };
  return undefined;
}

/**
 * The code Azure gave, such as `BadRequest`.
 *
 * @param error - Whatever a `catch` received.
 * @returns The string in `error.code` of the provider's body, or `undefined`
 * for a refusal by the proxy, for an error without a body, for a body without a
 * string in `error.code`, and for anything that is not an `HttpRequestError`.
 */
export function providerCodeOf(error: unknown): string | undefined {
  if (!isHttpRequestError(error) || isProxyRefusal(error)) return undefined;
  const code = errorObjectOf(error.body)?.code;
  return typeof code === 'string' ? code : undefined;
}

function errorObjectOf(body: unknown): Record<string, unknown> | undefined {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return undefined;
  }
  const inner: unknown = body.error;
  if (typeof inner !== 'object' || inner === null) return undefined;
  return { ...inner };
}
